function useAddressFinderInputName(model) {
  return model === 'address'
    ? 'useAddressFinder'
    : `${model}[useAddressFinder]`;
}

function administrativeAreaFieldName(model) {
  return model === 'address'
    ? 'administrativeArea'
    : `${model}[administrativeArea]`;
}

const DEFAULT_AU_STATES = {
  ACT: 'Australian Capital Territory',
  NSW: 'New South Wales',
  NT: 'Northern Territory',
  QLD: 'Queensland',
  SA: 'South Australia',
  TAS: 'Tasmania',
  VIC: 'Victoria',
  WA: 'Western Australia',
};

function getStatesForCountry(countryCode) {
  const states = window.__PROTAT_ADDRESS_STATES__ ?? {};
  const countryStates = states[countryCode] ?? null;

  if (countryStates && Object.keys(countryStates).length > 0) {
    return countryStates;
  }

  if (countryCode === 'AU') {
    return DEFAULT_AU_STATES;
  }

  return null;
}

function getActiveAdministrativeAreaValue(select, text) {
  if (select.name) {
    return select.value;
  }

  return text.value;
}

function dispatchActiveAdministrativeAreaChange(model) {
  const select = document.querySelector(
    `[data-administrative-area-select][data-address-model="${CSS.escape(
      model
    )}"]`
  );
  const text = document.querySelector(
    `[data-administrative-area-text][data-address-model="${CSS.escape(model)}"]`
  );
  const activeInput = select?.name ? select : text;

  if (activeInput) {
    activeInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function setAdministrativeAreaRequired(model, countryCode) {
  const field = document.querySelector(
    `[data-administrative-area-field][data-address-model="${CSS.escape(
      model
    )}"]`
  );

  if (!field) {
    return;
  }

  field.classList.toggle('field--required', countryCode === 'AU');
}

function setAdministrativeAreaFieldMode(model, countryCode) {
  const select = document.querySelector(
    `[data-administrative-area-select][data-address-model="${CSS.escape(
      model
    )}"]`
  );
  const text = document.querySelector(
    `[data-administrative-area-text][data-address-model="${CSS.escape(model)}"]`
  );

  if (!select || !text) {
    return;
  }

  setAdministrativeAreaRequired(model, countryCode);

  const fieldName = administrativeAreaFieldName(model);
  const states = getStatesForCountry(countryCode);
  const hasStates = states && Object.keys(states).length > 0;
  const currentValue =
    getActiveAdministrativeAreaValue(select, text) ||
    select.dataset.selected ||
    text.value ||
    '';

  if (hasStates) {
    const matchedValue =
      currentValue && Object.prototype.hasOwnProperty.call(states, currentValue)
        ? currentValue
        : '';

    select.innerHTML = '<option value="">Please select...</option>';
    Object.entries(states).forEach(([key, label]) => {
      const option = document.createElement('option');
      option.value = key;
      option.textContent = label;
      if (key === matchedValue) {
        option.selected = true;
      }
      select.appendChild(option);
    });

    select.style.display = '';
    select.name = fieldName;
    text.style.display = 'none';
    text.removeAttribute('name');
    text.value = matchedValue;
    select.dataset.selected = matchedValue;
    dispatchActiveAdministrativeAreaChange(model);
    return;
  }

  select.style.display = 'none';
  select.removeAttribute('name');
  select.innerHTML = '<option value="">Please select...</option>';
  text.style.display = '';
  text.name = fieldName;
  text.value = currentValue;
  dispatchActiveAdministrativeAreaChange(model);
}

export function setAddressEntryMode(
  model,
  manualActive,
  { updateHiddenInput = true } = {}
) {
  const useAddressFinderInput = document.querySelector(
    `[name="${useAddressFinderInputName(model)}"]`
  );

  if (updateHiddenInput && useAddressFinderInput) {
    useAddressFinderInput.value = manualActive ? '0' : '1';
  }

  const toggleOff = document.querySelector(
    `[data-manual-address-toggle-off][data-address-model="${model}"]`
  );
  const toggleOn = document.querySelector(
    `[data-manual-address-toggle-on][data-address-model="${model}"]`
  );

  if (toggleOff) {
    toggleOff.style.display = manualActive ? 'none' : '';
  }

  if (toggleOn) {
    toggleOn.style.display = manualActive ? 'flex' : 'none';
  }

  document
    .querySelectorAll(`[data-manual-address][data-address-model="${model}"]`)
    .forEach((field) => {
      const container = field.closest('.field');
      if (container) {
        container.style.display = manualActive ? '' : 'none';
      }
    });

  document
    .querySelectorAll(
      `[data-administrative-area-field][data-address-model="${model}"]`
    )
    .forEach((field) => {
      field.style.display = manualActive ? '' : 'none';
    });

  document
    .querySelectorAll(`[data-addressfinder][data-address-model="${model}"]`)
    .forEach((field) => {
      const container = field.closest('.field');
      if (container) {
        container.style.display = manualActive ? 'none' : '';
      }
    });
}

function syncAddressEntryModeForCountry(model, countryCode) {
  const effectiveCountryCode = countryCode || 'AU';
  const isAu = effectiveCountryCode === 'AU';
  const useAddressFinderInput = document.querySelector(
    `[name="${useAddressFinderInputName(model)}"]`
  );

  if (!isAu) {
    setAddressEntryMode(model, true);
    refreshAdministrativeAreaForModel(model);
    return;
  }

  const manualActive = useAddressFinderInput?.value !== '1';
  setAddressEntryMode(model, manualActive, { updateHiddenInput: false });

  if (manualActive) {
    refreshAdministrativeAreaForModel(model);
  }
}

function toggleAddressFinderForCountry(model, countryCode) {
  syncAddressEntryModeForCountry(model, countryCode);
}

function updatePostcodeField(model, countryCode) {
  const postcode = document.querySelector(
    `[data-address-postcode][data-address-model="${CSS.escape(model)}"]`
  );

  if (!postcode) {
    return;
  }

  if (countryCode === 'AU') {
    postcode.setAttribute('pattern', '\\d*');
    postcode.setAttribute('inputmode', 'numeric');
  } else {
    postcode.removeAttribute('pattern');
    postcode.removeAttribute('inputmode');
  }
}

function initAddressCountrySelect(countrySelect) {
  const model = countrySelect.dataset.addressModel;
  if (!model) {
    return;
  }

  const onCountryChange = () => {
    const countryCode = countrySelect.value;
    setAdministrativeAreaFieldMode(model, countryCode);
    toggleAddressFinderForCountry(model, countryCode);
    updatePostcodeField(model, countryCode);
    countrySelect.dispatchEvent(
      new CustomEvent('address-country-change', { bubbles: true })
    );
  };

  countrySelect.addEventListener('change', onCountryChange);
  onCountryChange();
}

export function refreshAdministrativeAreaForModel(model) {
  const countrySelect = document.querySelector(
    `[data-address-country-select][data-address-model="${CSS.escape(model)}"]`
  );

  if (!countrySelect) {
    return;
  }

  setAdministrativeAreaFieldMode(model, countrySelect.value);
}

export function initAddressCountryFields() {
  document
    .querySelectorAll('[data-address-country-select]')
    .forEach(initAddressCountrySelect);
}

document.addEventListener('DOMContentLoaded', initAddressCountryFields);
